import React, { useEffect, useRef, useState } from 'react'
import { Input } from 'components/ds/Input'
import { Radio, Switch } from 'components/ds/Choice'
import Button from 'components/ds/Button'
import { Toast } from 'components/shared/Toaster'
import { getDeclineEmailSettings, previewDeclineEmail, updateDeclineEmailSettings } from 'actions/client'
import styles from './DeclineEmailSettingsTab.module.scss'

const TOKEN = '{businessName}'
const SUBJECT_MAX = 200
const MESSAGE_MAX = 5000
const EMAIL_PATTERN = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]{2,}$/
const DEFAULT_PREVIEW_BUSINESS_NAME = 'Sample Business LLC'

const fmt = n => Number(n).toLocaleString('en-US')
const fill = (text, name) => String(text || '').split(TOKEN).join(name)

// Force the template's dark-mode rules on or off, whatever the viewer's system setting is.
const withColorScheme = (html, dark) =>
    html.replace(/\(prefers-color-scheme:\s*dark\)/g, dark ? '(min-width: 0px)' : '(max-width: 0px)')

const toForm = res => ({
    subject: res?.subject || '',
    message: res?.message || '',
    replyTo: res?.replyTo || 'ME',
    companyEmail: res?.companyEmail || '',
})

/**
 * Saved defaults for the applicant decline email (flag APPLICATION_DECLINE_EMAIL), with a live preview rendered
 * by the backend from the real template. The decline dialog pre-fills from these; users can still edit per send.
 */
const DeclineEmailSettingsTab = () => {
    const [info, setInfo] = useState(null)          // read-only values from the server
    const [saved, setSaved] = useState(null)        // last saved form, for Discard and "unsaved changes"
    const [form, setForm] = useState(null)
    const [saving, setSaving] = useState(false)
    const [justSaved, setJustSaved] = useState(false)
    const [submitted, setSubmitted] = useState(false)
    const [companyTouched, setCompanyTouched] = useState(false)
    const [previewHtml, setPreviewHtml] = useState('')
    const [loadFailed, setLoadFailed] = useState(false)
    const [previewFailed, setPreviewFailed] = useState(false)
    const [frameHeight, setFrameHeight] = useState(560)
    // Only for the preview, never saved. Dark starts at the viewer's system theme.
    const [sampleBusinessName, setSampleBusinessName] = useState(DEFAULT_PREVIEW_BUSINESS_NAME)
    const [previewDark, setPreviewDark] = useState(() => !!window.matchMedia?.('(prefers-color-scheme: dark)').matches)
    const subjectRef = useRef(null)
    const messageRef = useRef(null)
    const frameRef = useRef(null)

    const load = res => {
        setInfo(res || {})
        setSaved(toForm(res))
        setForm(toForm(res))
    }

    // A failed load must not turn into an editable blank form: saving it would wipe the client's defaults.
    const fetchSettings = () => {
        setLoadFailed(false)
        getDeclineEmailSettings().then(load).catch(() => setLoadFailed(true))
    }

    useEffect(fetchSettings, [])

    const previewBusinessName = sampleBusinessName.trim() || DEFAULT_PREVIEW_BUSINESS_NAME

    // Re-render the preview shortly after typing stops.
    useEffect(() => {
        if (!form) return
        const timer = setTimeout(() => {
            previewDeclineEmail({ message: form.message, replyTo: form.replyTo, businessName: previewBusinessName })
                .then(res => { setPreviewHtml(res?.html || ''); setPreviewFailed(false) })
                .catch(() => setPreviewFailed(true))
        }, 400)
        return () => clearTimeout(timer)
    }, [form?.message, form?.replyTo, previewBusinessName]) // eslint-disable-line react-hooks/exhaustive-deps

    if (loadFailed) {
        return (
            <div className={styles.card}>
                <div className={styles.loadError}>
                    <span>The decline email settings couldn't be loaded, so they can't be edited right now.</span>
                    <Button variant="secondary" onClick={fetchSettings}>Try again</Button>
                </div>
            </div>
        )
    }

    if (!form) return null

    const update = patch => { setForm({ ...form, ...patch }); setJustSaved(false) }

    // Insert {businessName} at the cursor as if typed: execCommand keeps it in the browser's undo history
    // (Ctrl+Z works), respects maxLength and fires the normal onChange. Fallback for browsers without it.
    const insertToken = (field, ref, max) => {
        const el = ref.current
        if (!el) return
        el.focus()
        if (document.execCommand && document.execCommand('insertText', false, TOKEN)) return
        const value = form[field]
        const start = el.selectionStart ?? value.length
        const end = el.selectionEnd ?? value.length
        update({ [field]: (value.slice(0, start) + TOKEN + value.slice(end)).slice(0, max) })
    }

    const isCompany = form.replyTo === 'COMPANY'
    const companyEmail = form.companyEmail.trim()
    const companyBlocking = isCompany && (!companyEmail || !EMAIL_PATTERN.test(companyEmail))
    const companyError = isCompany && (submitted || companyTouched)
        ? (!companyEmail ? 'Enter a company email, or choose another reply option.'
            : !EMAIL_PATTERN.test(companyEmail) ? 'Enter a valid email address, such as credit@yourcompany.com.' : null)
        : null
    const suggestedCompanyEmail = info.suggestedCompanyEmail
    const showSuggestion = isCompany && suggestedCompanyEmail
        && suggestedCompanyEmail.toLowerCase() !== companyEmail.toLowerCase()

    const clean = ['subject', 'message', 'replyTo', 'companyEmail'].every(k => form[k] === saved[k])
    const messageEdited = info.builtInMessage && form.message !== info.builtInMessage
    const status = companyError ? 'Fix the highlighted field to save.'
        : justSaved ? 'Changes saved.' : clean ? 'No unsaved changes.' : 'You have unsaved changes.'

    const save = async () => {
        if (companyBlocking) {
            setSubmitted(true)
            return
        }
        setSaving(true)
        try {
            const res = await updateDeclineEmailSettings({
                subject: form.subject,
                message: form.message,
                replyTo: form.replyTo,
                companyEmail,
            })
            load(res)
            setSubmitted(false)
            setCompanyTouched(false)
            setJustSaved(true)
            Toast({ type: 'success', message: 'Decline email settings saved' })
        } catch (e) {
            Toast({ type: 'error', message: 'Could not save the decline email defaults' })
        } finally {
            setSaving(false)
        }
    }

    const discard = () => {
        setForm(saved)
        setSubmitted(false)
        setCompanyTouched(false)
        setJustSaved(false)
    }

    const measureFrame = () => {
        const height = frameRef.current?.contentDocument?.documentElement?.scrollHeight
        if (height) setFrameHeight(height)
    }

    const replyOptions = [
        { value: 'ME', label: 'The user who declines', caption: "Replies reach the underwriter's own inbox." },
        { value: 'COMPANY', label: 'Company email', caption: 'Replies reach one shared inbox.' },
        { value: 'NONE', label: "Don't allow replies", caption: "The email footer says the mailbox isn't monitored." },
    ]

    return (
        <div className={styles.card}>
            <header className={styles.head}>
                <h3 className={styles.title}>Decline email</h3>
                <span className={styles.caption}>
                    Defaults for the email an underwriter can send when they decline an application. The decline
                    dialog pre-fills from these, and each email can still be edited before it goes out.
                </span>
            </header>

            <div className={styles.columns}>
                <div className={styles.form}>
                    <div className={styles.field}>
                        <div className={styles.labelRow}>
                            <label className={styles.label} htmlFor="decline-email-subject">Subject</label>
                            <Button variant="ghost" size="small" onClick={() => insertToken('subject', subjectRef, SUBJECT_MAX)}>
                                + Business name
                            </Button>
                        </div>
                        <Input
                            id="decline-email-subject"
                            ref={subjectRef}
                            maxLength={SUBJECT_MAX}
                            value={form.subject}
                            onChange={e => update({ subject: e.target.value })}
                        />
                        <div className={styles.hintRow}>
                            <span className={styles.hint}>{TOKEN} is replaced with each applicant's business name.</span>
                            <span className={styles.count}>{fmt(form.subject.length)} / {fmt(SUBJECT_MAX)}</span>
                        </div>
                    </div>

                    <div className={styles.field}>
                        <div className={styles.labelRow}>
                            <label className={styles.label} htmlFor="decline-email-message">Message</label>
                            <div className={styles.labelActions}>
                                {messageEdited && (
                                    <Button variant="ghost" size="small" onClick={() => update({ message: info.builtInMessage })}>
                                        Restore built-in
                                    </Button>
                                )}
                                <Button variant="ghost" size="small" onClick={() => insertToken('message', messageRef, MESSAGE_MAX)}>
                                    + Business name
                                </Button>
                            </div>
                        </div>
                        <Input
                            id="decline-email-message"
                            ref={messageRef}
                            multiline
                            rows={10}
                            maxLength={MESSAGE_MAX}
                            placeholder={info.builtInMessage}
                            value={form.message}
                            onChange={e => update({ message: e.target.value })}
                        />
                        <div className={styles.hintRow}>
                            <span className={styles.hint}>
                                {form.message.trim() ? 'A blank line starts a new paragraph.' : 'Empty. The built-in message will be sent.'}
                            </span>
                            <span className={styles.count}>{fmt(form.message.length)} / {fmt(MESSAGE_MAX)}</span>
                        </div>
                    </div>

                    <fieldset className={styles.replies}>
                        <legend className={styles.label}>Replies go to</legend>
                        {replyOptions.map(o => (
                            <div key={o.value} className={styles.replyOption}>
                                <Radio
                                    name="declineEmailDefaultReplyTo"
                                    label={o.label}
                                    checked={form.replyTo === o.value}
                                    onChange={() => update({ replyTo: o.value })}
                                />
                                <span className={styles.replyCaption}>{o.caption}</span>
                                {o.value === 'COMPANY' && isCompany && (
                                    <div className={styles.companyBlock}>
                                        <Input
                                            id="decline-email-company"
                                            label="Company reply-to email"
                                            required
                                            type="email"
                                            placeholder="credit@yourcompany.com"
                                            value={form.companyEmail}
                                            onChange={e => update({ companyEmail: e.target.value })}
                                            onBlur={() => setCompanyTouched(true)}
                                            error={companyError}
                                        />
                                        {showSuggestion && (
                                            <div className={styles.suggestion}>
                                                <button type="button" className={styles.suggestionLink}
                                                        onClick={() => update({ companyEmail: suggestedCompanyEmail })}>
                                                    Use client email
                                                </button>
                                                <span className={styles.suggestionEmail}>{suggestedCompanyEmail}</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </fieldset>
                </div>

                <div className={styles.previewPane}>
                    <div className={styles.previewBar}>
                        <span className={styles.eyebrow}>Preview</span>
                        <div className={styles.previewControls}>
                            <label className={styles.sampleLabel}>
                                <span>Sample business name</span>
                                <Input
                                    className={styles.sampleInput}
                                    maxLength={200}
                                    value={sampleBusinessName}
                                    onChange={e => setSampleBusinessName(e.target.value)}
                                />
                            </label>
                            <Switch label="Dark" checked={previewDark} onChange={e => setPreviewDark(e.target.checked)} />
                        </div>
                    </div>
                    <div className={styles.previewFrameWrap}>
                        <div className={styles.envelope}>
                            <span className={styles.caption}>From</span>
                            <span className={styles.envelopeValue}>{info.clientName} &lt;{info.fromAddress}&gt;</span>
                            <span className={styles.caption}>Subject</span>
                            <span className={styles.envelopeSubject}>
                                {fill(form.subject.trim() ? form.subject : info.builtInSubject, previewBusinessName)}
                            </span>
                        </div>
                        {previewFailed && (
                            <div className={styles.previewError}>The preview couldn't be updated. It will retry when you edit.</div>
                        )}
                        <iframe
                            ref={frameRef}
                            title="Decline email preview"
                            className={styles.previewFrame}
                            style={{ height: frameHeight }}
                            srcDoc={withColorScheme(previewHtml, previewDark)}
                            sandbox="allow-same-origin"
                            onLoad={measureFrame}
                        />
                    </div>
                </div>
            </div>

            <footer className={styles.foot}>
                <span className={companyError ? styles.statusError : styles.caption}>{status}</span>
                <div className={styles.footActions}>
                    <Button variant="secondary" disabled={clean} onClick={discard}>Discard changes</Button>
                    <Button variant="primary" onClick={save} loading={saving}>Save changes</Button>
                </div>
            </footer>
        </div>
    )
}

export default DeclineEmailSettingsTab
